import React from "react";
import { Column, Row } from "@components/Flex";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import Input from "@components/Input";
import Button from "@components/Button";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useLang } from "@src/hooks/useLang";
import { useProfileScreenVM } from "@screens/ProfileScreen/ProfileScreenVM";
import Loading from "@components/Loading";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

interface IProps {
  loading?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  onChangePassword?: () => void;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  justify-content: space-between;
`;

const InputGrid = styled.div`
  width: 100%;
  display: grid;
  column-gap: 16px;
  grid-template-columns: 1fr 1fr 1fr;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
  }
`;

const ProfileScreenForm: React.FC<IProps> = ({
  loading,
  onSave,
  onCancel,
  onChangePassword,
}) => {
  const lang = useLang();
  const vm = useProfileScreenVM();

  const setName = (value: string) =>
    vm.formData &&
    vm.setFormData({ 
      ...vm.formData,
      full_name: value
    });

  const setPhoneNumber = (value: string) =>
    vm.formData &&
    vm.setFormData({
      ...vm.formData,
      phone_number: value
    });

  const setOrganization = (value: string) =>
    vm.formData &&
    vm.setFormData({
      ...vm.formData,
      organization_name: value
    });

  return (
    <Root>
      <Column crossAxisSize="max">
        <Text
          type="heading"
          size={11}
          color="#899093"
          style={{ textTransform: "uppercase" }}
        >
          {lang.profile.companyTitle}
        </Text>
        <SizedBox height={8} />
        <InputGrid>
          <Input
            value={vm.formData?.organization_name}
            onChange={(e) => setOrganization(e.target.value)}
            style={{ flex: 1, height: 36 }}
            placeholder={lang.profile.organizationNamePlaceholder}
            label={lang.profile.organizationNameTitle}
          />
          <Input
            value={vm.formData?.phone_number}
            onChange={(e) => setPhoneNumber(e.target.value)}
            style={{ flex: 1, height: 36 }}
            placeholder={lang.profile.contactPhoneFill}
            label={lang.profile.contactPhoneTitle}
          />
          <Input
            disabled
            value={vm.formData?.email}
            onChange={() => {}}
            style={{ flex: 1, height: 36 }}
            placeholder={lang.profile.emailFill}
            label={lang.profile.emailTitle}
          />
        </InputGrid>
        <SizedBox height={24} />
        <Text
          type="heading"
          size={11}
          color="#899093"
          style={{ textTransform: "uppercase" }}
        >
          {lang.profile.personalTitle}
        </Text>
        <SizedBox height={8} />
        <InputGrid>
          <Input
            value={vm.formData?.full_name}
            onChange={(e) => setName(e.target.value)}
            style={{ flex: 1, height: 36 }}
            placeholder={lang.profile.nameFill}
            label={lang.profile.nameTitle}
          />
        </InputGrid>
        <SizedBox height={24} />
        <Row justifyContent="start">
          <Button
            onClick={onChangePassword}
            kind="secondary"
            type="button"
            style={{ padding: "0 16px", maxWidth: "fit-content" }}
          >
            {lang.profile.changePasswordButton}
          </Button>
        </Row>
      </Column>
      <Row justifyContent="flex-end">
        <Button
          onClick={onCancel}
          kind="outline"
          style={{ padding: "0 20px", maxWidth: "fit-content" }}
        >
          {lang.profile.profileCancelButton}
        </Button>
        <SizedBox width={8} />
        <Button
          disabled={loading}
          onClick={onSave}
          style={{ padding: "0 16px", width: 210 }}
        >
          {!loading ? lang.profile.profileSaveButton : <Loading />}
        </Button>
      </Row>
    </Root>
  );
};
export default observer(ProfileScreenForm);
